import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Loader2, BookOpen, Lightbulb } from "lucide-react";

const SUBJECTS = ["English", "Kannada", "Mathematics", "Science", "Social Studies"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * StudentAnswerSheetViewer — opened when a student taps a topper's photo on the
 * leaderboard. Pick a subject to see how the topper wrote their answers.
 */
export default function StudentAnswerSheetViewer({ open, onClose, student, examKey, examLabel }) {
  const [available, setAvailable] = useState([]);
  const [subject, setSubject] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => {
    if (!open || !student?.student_id || !examKey) return;
    setSubject(null);
    setSheets([]);
    setLoadingSubs(true);
    api.get("/answer-sheets/subjects", { params: { student_id: student.student_id, exam_key: examKey } })
      .then(({ data }) => setAvailable(data.subjects || []))
      .catch(() => setAvailable([]))
      .finally(() => setLoadingSubs(false));
  }, [open, student?.student_id, examKey]);

  useEffect(() => {
    if (!open || !subject) { setSheets([]); return; }
    setLoading(true);
    api.get("/answer-sheets", { params: { student_id: student.student_id, exam_key: examKey, subject } })
      .then(({ data }) => setSheets(data.items || []))
      .catch(() => setSheets([]))
      .finally(() => setLoading(false));
  }, [open, subject, student?.student_id, examKey]);

  const fileUrl = (id) => `${BACKEND}/api/answer-sheets/file/${id}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="student-answersheet-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-amber-500" /> {student?.name}'s answer sheets</DialogTitle>
          <DialogDescription>See how a topper wrote their answers{examLabel ? ` — ${examLabel}` : ""}. Pick a subject.</DialogDescription>
        </DialogHeader>

        {loadingSubs ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : available.length === 0 ? (
          <div className="py-8 text-center" data-testid="viewer-empty">
            <Lightbulb className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No answer sheets have been shared for this student yet.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="viewer-subjects">
              {SUBJECTS.filter((s) => available.includes(s)).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSubject(s)}
                  data-testid={`viewer-subject-${s}`}
                  className={"px-3 h-9 rounded-full text-sm font-semibold border transition-colors " +
                    (subject === s ? "bg-amber-500 border-amber-500 text-white" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-300")}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mt-3 space-y-3" data-testid="viewer-sheets">
              {!subject ? (
                <p className="text-sm text-slate-400 text-center py-6">Select a subject to view answer sheets.</p>
              ) : loading ? (
                <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : sheets.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No sheets for {subject}.</p>
              ) : (
                sheets.map((sh) => {
                  const isPdf = (sh.mime || "").includes("pdf");
                  return isPdf ? (
                    <a key={sh.id} href={fileUrl(sh.id)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-amber-300" data-testid={`viewer-file-${sh.id}`}>
                      <FileText className="h-8 w-8 text-rose-500" />
                      <span className="text-sm text-sky-600">Open PDF · {sh.name}</span>
                    </a>
                  ) : (
                    <a key={sh.id} href={fileUrl(sh.id)} target="_blank" rel="noreferrer" data-testid={`viewer-file-${sh.id}`}>
                      <img src={fileUrl(sh.id)} alt={sh.name} className="w-full rounded-xl border border-slate-200 dark:border-slate-700" />
                    </a>
                  );
                })
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
